/**
 * Sistema de unidades de medida (espejo del backend).
 *
 * Storage: stock y precios en el backend SIEMPRE estan en la base unit:
 *   - count  -> unit
 *   - weight -> gram
 *
 * Display: el usuario elige `display_unit` y vemos las cosas asi.
 */

export type Dimension = 'count' | 'weight';
export type Unit = 'unit' | 'gram' | 'kilogram';

export const UNIT_FACTORS: Record<Unit, number> = {
  unit: 1,
  gram: 1,
  kilogram: 1000,
};

export const UNIT_DIMENSION: Record<Unit, Dimension> = {
  unit: 'count',
  gram: 'weight',
  kilogram: 'weight',
};

export const DIMENSION_BASE_UNIT: Record<Dimension, Unit> = {
  count: 'unit',
  weight: 'gram',
};

export const UNIT_LABEL: Record<Unit, string> = {
  unit: 'unidad',
  gram: 'gramo',
  kilogram: 'kilogramo',
};

export const UNIT_LABEL_SHORT: Record<Unit, string> = {
  unit: 'u',
  gram: 'g',
  kilogram: 'kg',
};

export const DIMENSION_LABEL: Record<Dimension, string> = {
  count: 'Cantidad (unidades)',
  weight: 'Peso',
};

/** Lista de unidades validas para una dimension. */
export function compatibleUnits(dimension: Dimension): Unit[] {
  return (Object.keys(UNIT_DIMENSION) as Unit[]).filter((u) => UNIT_DIMENSION[u] === dimension);
}

/** Convierte una cantidad de la unidad indicada a la base unit de su dimension. */
export function toBase(quantity: number, unit: Unit): number {
  return quantity * UNIT_FACTORS[unit];
}

/** Convierte una cantidad en base unit a la unidad target. */
export function fromBase(quantityBase: number, targetUnit: Unit): number {
  return quantityBase / UNIT_FACTORS[targetUnit];
}

/** Convierte directamente entre dos unidades de la misma dimension. */
export function convert(quantity: number, fromUnit: Unit, toUnit: Unit): number {
  if (UNIT_DIMENSION[fromUnit] !== UNIT_DIMENSION[toUnit]) {
    throw new Error(`No se puede convertir de ${fromUnit} a ${toUnit} (dimensiones distintas)`);
  }
  return toBase(quantity, fromUnit) / UNIT_FACTORS[toUnit];
}

/**
 * Formatea un stock guardado en base_unit segun la display_unit elegida.
 * Ej: formatStock(10000, 'kilogram') → "10 kg"
 *     formatStock(24, 'unit') → "24 u"
 */
export function formatStock(stockInBase: number, displayUnit: Unit, decimals = 2): string {
  const value = fromBase(stockInBase, displayUnit);
  const formatted = decimals === 0 ? Math.round(value).toString() : value.toFixed(decimals).replace(/\.?0+$/, '');
  return `${formatted} ${UNIT_LABEL_SHORT[displayUnit]}`;
}

/**
 * Formatea un precio guardado por base_unit segun la display_unit.
 * Ej: priceInBase=0.5, displayUnit=kilogram -> "$500/kg"
 *     priceInBase=2400, displayUnit=unit    -> "$2400/u"
 */
export function formatPricePerUnit(priceInBase: number | string | null | undefined, displayUnit: Unit, showUnit: boolean = true): string {
  if (priceInBase === null || priceInBase === undefined || priceInBase === '') return '-';
  const num = typeof priceInBase === 'string' ? parseFloat(priceInBase) : priceInBase;
  if (isNaN(num)) return '-';
  const valuePerDisplay = num * UNIT_FACTORS[displayUnit];
  const formatted = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(valuePerDisplay);
  return showUnit ? `$${formatted}/${UNIT_LABEL_SHORT[displayUnit]}` : `$${formatted}`;
}

/**
 * Convierte un precio que el usuario ingresa por display_unit a precio por base_unit.
 * Ej: user pone $500/kg con displayUnit=kilogram -> guardamos 0.5 ($/g)
 */
export function pricePerBaseFromDisplay(pricePerDisplay: number, displayUnit: Unit): number {
  return pricePerDisplay / UNIT_FACTORS[displayUnit];
}

/**
 * Convierte un precio guardado por base_unit a precio por display_unit (para mostrar en inputs).
 */
export function pricePerDisplayFromBase(pricePerBase: number, displayUnit: Unit): number {
  return pricePerBase * UNIT_FACTORS[displayUnit];
}

export const UNIT_OPTIONS_BY_DIMENSION: Record<Dimension, { value: Unit; label: string; short: string }[]> = {
  count: [{ value: 'unit', label: 'Unidad', short: 'u' }],
  weight: [
    { value: 'gram', label: 'Gramo', short: 'g' },
    { value: 'kilogram', label: 'Kilogramo', short: 'kg' },
  ],
};
